import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { transformations } from '../data/transformations';
import { interiorBeforeAfterPairs } from '../assets/home_page_assets/interiorBeforeAfterImages';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import HeroVideo, { heroPoster } from '../components/HeroVideo';
import ShopTheFurnitureShowcase from '../components/ShopTheFurnitureShowcase';
import FloorPlanToolsHighlightSection from '../components/FloorPlanToolsHighlightSection';
import SmartAiDesignHighlightSection from '../components/SmartAiDesignHighlightSection';
import HomeAudienceSection from '../components/HomeAudienceSection';
import WhyChooseMyRoomsAISection from '../components/WhyChooseMyRoomsAISection';
import PricingSection from '../components/PricingSection';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import AuthAwareLink from '../components/AuthAwareLink';
import Seo from '../seo/Seo';
import { PAGE_SEO } from '../seo/pageSeoConfig';
import {
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from '../seo/structuredData';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';

const homeSeo = PAGE_SEO['/'];

function LandingPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.pathname, location.hash]);

  return (
    <div className="page landing-bg" id="home">
      <Seo
        title={homeSeo.title}
        description={homeSeo.description}
        canonicalPath="/"
        structuredData={[
          buildOrganizationSchema(),
          buildSoftwareApplicationSchema(),
          getSeoFaqSchema('/'),
        ].filter(Boolean)}
        preloadImages={[{ href: heroPoster, type: 'image/webp', fetchPriority: 'high' }]}
      />
      <Navbar />


      <section className="hero container section-grid">
        <div className="hero-copy">
          <span className="badge">
            AI-powered home design tools
          </span>
          <h1>Everything You Need for Home Design in One <span style={{ color: '#237CE4' }}>A</span>I Platform.</h1>
          <p>
            Design interiors, exteriors, landscapes, floor plans, architecture, and furniture all in one place.
            Replace multiple design tools and subscriptions with a single AI platform built to help you plan,
            visualize, and transform every part of your home.
          </p>
          <div className="hero-actions">
            <Link to="/ai-tools/ai-room-decoration" className="btn btn-primary hero-btn-start">
              <Sparkles size={18} strokeWidth={2} aria-hidden="true" className="hero-btn-icon" />
              Start Room Decoration
            </Link>
            <AuthAwareLink spaPath="/features" staticPath="/features/" className="btn btn-ghost">
              See Features
            </AuthAwareLink>
          </div>
        </div>
        <div className="hero-visual">
          <div className="new-design-workspace-preview-guide hero-preview-guide">
            <div className="new-design-workspace-preview-tool-media new-design-workspace-preview-tool-media--vertical-scan hero-media-card">
              <div className="new-design-workspace-preview-offset-rectangle" aria-hidden="true" />
              <HeroVideo />
            </div>
          </div>
        </div>
      </section>

      <HomeAudienceSection />

      <FloorPlanToolsHighlightSection />

      <section
        id="interior-transformation"
        className="home-transformations-preview container section"
        aria-label="Instant interior transformation"
      >
        <div className="section-heading">
          <h2>
            Instant Interior{' '}
            <span className="shop-furniture-showcase__title-accent shop-furniture-showcase__title-accent--gradient">
              Transformation
            </span>
          </h2>
          <p>Fill any space with furniture and redesign the decor.</p>
        </div>
        <div className="transformation-preview-grid">
          {interiorBeforeAfterPairs.slice(0, 3).map((item) => (
            <article key={item.id} className="transformation-preview-card card">
              <BeforeAfterSlider
                before={item.beforeImage}
                after={item.afterImage}
                loading="lazy"
              />
            </article>
          ))}
        </div>
        <div className="home-transformations-cta-wrap">
          <Link to="/ai-tools/ai-room-decoration" className="btn btn-primary home-transformations-cta">
            <Sparkles size={16} strokeWidth={2} aria-hidden="true" />
            Start Room Decoration
          </Link>
        </div>
      </section>

      <SmartAiDesignHighlightSection />

      <section id="features" className="home-transformations-preview container section">
        <div className="section-heading">
          <h2>
            Preview Real{' '}
            <span className="shop-furniture-showcase__title-accent shop-furniture-showcase__title-accent--gradient">
              Transformations
            </span>
          </h2>
          <p>
            Explore real before-and-after room transformations created with our AI model.
          </p>
        </div>
        <div className="transformation-preview-grid">
          {transformations.slice(0, 3).map((item) => (
            <article key={item.id} className="transformation-preview-card card">
              <BeforeAfterSlider
                before={item.beforeImage}
                after={item.afterImage}
                loading="lazy"
              />
            </article>
          ))}
        </div>
        <div className="home-transformations-cta-wrap">
          <Link to="/real-transformations" className="btn btn-primary home-transformations-cta">
            View More Transformations <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <WhyChooseMyRoomsAISection />

      <ShopTheFurnitureShowcase />

      <PricingSection id="pricing" bleed />

      <CollapsibleSeoContent {...getSeoContent('/')} />

      <Footer />
    </div>
  );
}

export default LandingPage;
